(() => {
  const doc = document;
  const root = doc.documentElement;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const focusSelector = 'a[href],button:not([disabled]),summary,input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  let activeLayer = null;
  let activeTrigger = null;
  let savedScrollY = 0;
  let drawerFocusVersion = 0;
  let focusRevision = 0;
  doc.addEventListener('focusin', () => { focusRevision += 1; });

  const formatCurrency = (amount) => {
    const currency = doc.body.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(doc.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(amount || 0));
    } catch (error) {
      return `$${Number(amount || 0).toFixed(2)}`;
    }
  };
  const money = (cents) => formatCurrency(Number(cents || 0) / 100);

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]);

  const imageUrl = (url, width) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const visibleFocusables = (container) => [...container.querySelectorAll(focusSelector)].filter((element) => {
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return element.tabIndex >= 0 && rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
  });

  const lockScroll = () => {
    if (root.classList.contains('po-scroll-locked')) return;
    savedScrollY = window.scrollY;
    root.style.setProperty('--po-locked-scroll', `${savedScrollY}px`);
    root.classList.add('po-scroll-locked');
  };

  const unlockScroll = () => {
    root.classList.remove('po-scroll-locked');
    root.style.removeProperty('--po-locked-scroll');
    window.scrollTo(0, savedScrollY);
  };

  const closeDrawer = (drawer, restore = true) => {
    if (!drawer || drawer.dataset.open !== 'true') return;
    const version = ++drawerFocusVersion;
    const revision = focusRevision;
    drawer.dataset.open = 'false';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.querySelectorAll('details[open]').forEach((details) => { details.open = false; });
    drawer.querySelectorAll('[aria-expanded="true"][aria-controls]').forEach((control) => control.setAttribute('aria-expanded', 'false'));
    doc.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((control) => control.setAttribute('aria-expanded', 'false'));
    drawer.inert = true;
    if (activeLayer === drawer) activeLayer = null;
    unlockScroll();
    const trigger = activeTrigger;
    activeTrigger = null;
    if (restore && trigger?.isConnected) setTimeout(() => {
      if (version !== drawerFocusVersion || revision !== focusRevision || activeLayer || !trigger.isConnected) return;
      trigger.focus({ preventScroll: true });
    }, reduceMotion ? 0 : 610);
  };

  const openDrawer = (drawer, trigger, focus = true, focusClose = false) => {
    if (!drawer) return;
    if (activeLayer && activeLayer !== drawer) closeDrawer(activeLayer, false);
    const version = ++drawerFocusVersion;
    const revision = focusRevision;
    activeLayer = drawer;
    activeTrigger = trigger || doc.activeElement;
    drawer.inert = false;
    drawer.dataset.open = 'true';
    drawer.setAttribute('aria-hidden', 'false');
    doc.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((control) => control.setAttribute('aria-expanded', 'true'));
    lockScroll();
    if (focus || focusClose) {
      const target = focusClose ? drawer.querySelector('.po-drawer__close') : drawer.querySelector('[data-po-autofocus]') || drawer.querySelector('input[type="search"]') || drawer.querySelector('.po-drawer__close');
      const focusTarget = () => {
        if (version !== drawerFocusVersion || revision !== focusRevision || activeLayer !== drawer || drawer.dataset.open !== 'true' || !target?.isConnected) return;
        target.focus({ preventScroll: true });
      };
      requestAnimationFrame(focusTarget);
      setTimeout(focusTarget, reduceMotion ? 0 : 620);
      setTimeout(focusTarget, reduceMotion ? 20 : 520);
    }
  };

  doc.querySelectorAll('[data-po-drawer]').forEach((drawer) => {
    drawer.inert = true;
    drawer.querySelectorAll('[data-po-drawer-close], [data-po-drawer-overlay]').forEach((control) => control.addEventListener('click', () => closeDrawer(drawer)));
    drawer.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusables = visibleFocusables(drawer);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables.at(-1);
      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  });

  doc.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-po-drawer-open]');
    if (opener) {
      event.preventDefault();
      const drawer = doc.getElementById(opener.getAttribute('aria-controls') || opener.dataset.poDrawerOpen);
      if (drawer?.matches('[data-po-cart-drawer]')) { openCart(opener).catch(() => { window.location.href = cartDrawer.dataset.cartPageUrl; }); return; }
      const mobileSearch = matchMedia('(max-width: 989px)').matches && drawer?.id === 'SearchDrawer';
      openDrawer(drawer, opener, !mobileSearch, mobileSearch);
    }
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (activeLayer) {
      event.preventDefault();
      closeDrawer(activeLayer);
      return;
    }
    doc.querySelectorAll('[data-po-dropdown][open]').forEach((details) => {
      details.removeAttribute('open');
      delete details.dataset.poPinned;
    });
  });

  const header = doc.querySelector('[data-po-header]');
  if (header) {
    let lastY = window.scrollY;
    let ticking = false;
    const updateMegaTop = (details) => {
      const activeDetails = details || header.querySelector('[data-po-dropdown][open]');
      const anchor = activeDetails?.querySelector('summary') || header;
      const anchorRect = anchor.getBoundingClientRect();
      const top = `${Math.max(0, Math.round(anchorRect.bottom))}px`;
      header.style.setProperty('--po-mega-top', top);
      doc.documentElement.style.setProperty('--po-mega-top', top);
      if (activeDetails) activeDetails.style.setProperty('--po-dropdown-center', `${Math.round(anchorRect.left + (anchorRect.width / 2))}px`);
    };
    const updateHeader = () => {
      const y = window.scrollY;
      header.classList.toggle('is-compact', y > 150);
      updateMegaTop();
      if (matchMedia('(max-width: 989px)').matches) {
        header.classList.toggle('is-hidden', y > 170 && y > lastY + 4 && !activeLayer);
        if (y < lastY - 4 || y < 80) header.classList.remove('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    };
    addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateHeader);
    }, { passive: true });
    addEventListener('resize', () => updateMegaTop());
    updateHeader();

    const dropdowns = [...header.querySelectorAll('[data-po-dropdown]')];
    const closeDropdowns = (except) => dropdowns.forEach((item) => {
      if (item === except) return;
      item.removeAttribute('open');
      delete item.dataset.poPinned;
    });
    dropdowns.forEach((details) => {
      let closeTimer;
      const open = () => {
        clearTimeout(closeTimer);
        updateMegaTop(details);
        closeDropdowns(details);
        details.setAttribute('open', '');
      };
      const scheduleClose = () => {
        if (details.dataset.poPinned === 'true') return;
        closeTimer = setTimeout(() => details.removeAttribute('open'), 280);
      };
      details.addEventListener('pointerenter', open);
      details.addEventListener('pointerleave', scheduleClose);
      details.addEventListener('focusin', open);
      details.addEventListener('focusout', (event) => { if (!details.contains(event.relatedTarget)) scheduleClose(); });
      details.querySelector('summary')?.addEventListener('click', (event) => {
        event.preventDefault();
        const isPinned = details.dataset.poPinned === 'true';
        if (isPinned) {
          details.removeAttribute('open');
          delete details.dataset.poPinned;
          return;
        }
        closeDropdowns(details);
        details.dataset.poPinned = 'true';
        details.setAttribute('open', '');
      });
    });
    doc.addEventListener('pointerdown', (event) => { if (!event.target.closest('[data-po-dropdown]')) closeDropdowns(); });
  }

  doc.querySelectorAll('[data-po-mega-tabs]').forEach((mega) => {
    const tabs = [...mega.querySelectorAll('[data-po-mega-tab]')];
    const panels = [...mega.querySelectorAll('[data-po-mega-panel]')];
    const activate = (tab, focus = false) => {
      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
      if (focus) tab.focus();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('pointerenter', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        let target = null;
        if (event.key === 'ArrowRight') target = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') target = tabs[(index - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') target = tabs[0];
        if (event.key === 'End') target = tabs[tabs.length - 1];
        if (!target) return;
        event.preventDefault();
        activate(target, true);
      });
    });
  });

  doc.querySelectorAll('[data-po-mobile-menu]').forEach((menu) => {
    const child = menu.querySelector('[data-po-mobile-child-content]');
    const childTitle = menu.querySelector('[data-po-mobile-child-title]');
    menu.querySelectorAll('[data-po-mobile-next]').forEach((button) => button.addEventListener('click', () => {
      const source = doc.getElementById(button.getAttribute('aria-controls'));
      if (!source || !child) return;
      child.replaceChildren(...[...source.children].map((node) => node.cloneNode(true)));
      if (childTitle) childTitle.textContent = button.dataset.title || button.textContent.trim();
      menu.dataset.level = '1';
      menu.querySelector('[data-po-mobile-back]')?.focus();
    }));
    menu.querySelector('[data-po-mobile-back]')?.addEventListener('click', () => {
      menu.dataset.level = '0';
      const title = childTitle?.textContent?.trim();
      const returnControl = [...menu.querySelectorAll('[data-po-mobile-next]')].find((button) => button.dataset.title === title);
      setTimeout(() => returnControl?.focus(), reduceMotion ? 0 : 610);
    });
  });

  doc.querySelectorAll('[data-po-search-drawer]').forEach((drawer) => {
    const input = drawer.querySelector('[data-po-search-input]');
    const results = drawer.querySelector('[data-po-search-results]');
    const popular = drawer.querySelector('[data-po-search-popular]');
    const clear = drawer.querySelector('[data-po-search-clear]');
    let controller;
    let timer;
    const render = (products, term) => {
      if (!results) return;
      results.replaceChildren();
      popular?.toggleAttribute('hidden', Boolean(term));
      if (!term) return;
      if (!products.length) {
        const empty = doc.createElement('p');
        empty.className = 'po-search__empty';
        empty.textContent = drawer.dataset.empty.replace('[term]', term);
        results.append(empty);
      } else {
        products.forEach((product) => {
          const link = doc.createElement('a');
          link.className = 'po-search-card';
          link.href = product.url;
          link.innerHTML = `${product.image ? `<img src="${escapeHtml(imageUrl(product.image, 180))}" alt="" width="74" height="74" loading="lazy">` : ''}<span><strong>${escapeHtml(product.title)}</strong><small>${formatCurrency(product.price)}</small></span>`;
          results.append(link);
        });
        const all = doc.createElement('a');
        all.className = 'po-search__all';
        all.href = `${drawer.dataset.searchUrl}?q=${encodeURIComponent(term)}&type=product`;
        all.textContent = drawer.dataset.viewAll.replace('[term]', term);
        results.append(all);
      }
    };
    const search = () => {
      const term = input?.value.trim() || '';
      clear?.toggleAttribute('hidden', !term);
      if (!term) { render([], ''); return; }
      controller?.abort();
      controller = new AbortController();
      const endpoint = drawer.dataset.predictiveUrl.endsWith('.json') ? drawer.dataset.predictiveUrl : `${drawer.dataset.predictiveUrl}.json`;
      fetch(`${endpoint}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`, { signal: controller.signal })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Search failed')))
        .then((payload) => render(payload.resources?.results?.products || [], term))
        .catch((error) => { if (error.name !== 'AbortError') render([], term); });
    };
    input?.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(search, 180); });
    clear?.addEventListener('click', () => { input.value = ''; search(); input.focus(); });
    drawer.querySelectorAll('[data-po-popular-query]').forEach((button) => button.addEventListener('click', () => { input.value = button.dataset.poPopularQuery; search(); input.focus(); }));
  });

  const cartDrawer = doc.querySelector('[data-po-cart-drawer]');
  const cartContent = cartDrawer?.querySelector('[data-po-cart-content]');
  const cartRecommendation = cartDrawer?.querySelector('[data-po-cart-recommendation]');
  const cartUrl = cartDrawer?.dataset.cartUrl || '/cart.js';
  const referenceTemplateHandles = new Set(["starter-17-1-kit","complete-orange-24-4-kit","studio-21-4-kit","square-profile-21-5-practice-kit","double-row-21-5-practice-kit","classic-black-17-4-kit","leather-progression-17-3-kit","canvas-progression-17-3-kit"]);
  const previewProductUrl = (href) => {
    if (!doc.body.classList.contains('v22-theme')) return href;
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return href;
    const handle = url.pathname.split('/products/')[1].split('/')[0];
    if (referenceTemplateHandles.has(handle)) url.searchParams.delete('view');
    else url.searchParams.set('view', handle === 'starter-17-1-kit' ? 'starter-v22' : 'kit-v22');
    return `${url.pathname}${url.search}${url.hash}`;
  };
  const v22Summary = cartDrawer?.querySelector('[data-v22-cart-summary]');
  const v22CartError = cartDrawer?.querySelector('[data-v22-cart-error]');
  let cartBusy = false;
  let recommendationRevision = 0;
  let syncRecommendationControls = () => {};
  window.addEventListener('resize', () => syncRecommendationControls());
  const cartRecommendations = async (cart) => {
    const revision = ++recommendationRevision;
    if (!cart.items.length) return;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${encodeURIComponent(cart.items[0].product_id)}&limit=10&intent=related`);
      if (!response.ok) return;
      const data = await response.json();
      if (revision !== recommendationRevision) return;
      const products = (data.products || []).filter(product => product.available && !cart.items.some(line => line.product_id === product.id)).slice(0, 10);
      const host = cartContent.querySelector('[data-v22-recommendations]');
      if (!host || !products.length) return;
      host.innerHTML = `<div class="v22-cart-recommendation-head"><h3>${escapeHtml(cartDrawer.dataset.recommendLabel)}</h3><div class="v22-cart-carousel-controls"><button type="button" data-v22-rec-prev aria-label="${escapeHtml(cartDrawer.dataset.previousLabel)}" aria-controls="V22CartRecommendations">←</button><button type="button" data-v22-rec-next aria-label="${escapeHtml(cartDrawer.dataset.nextLabel)}" aria-controls="V22CartRecommendations">→</button></div></div><div id="V22CartRecommendations" class="v22-cart-recommendation-grid" tabindex="0" role="region" aria-label="${escapeHtml(cartDrawer.dataset.recommendLabel)}">${products.map(product => `<a href="${escapeHtml(previewProductUrl(product.url))}">${product.featured_image ? `<img src="${escapeHtml(imageUrl(product.featured_image,400))}" alt="${escapeHtml(product.title)}" width="200" height="200" loading="lazy">` : ''}<strong>${escapeHtml(product.title)}</strong><span>${money(product.price)}</span></a>`).join('')}</div>`;
      host.hidden = false;
      const grid = host.querySelector('.v22-cart-recommendation-grid');
      const previous = host.querySelector('[data-v22-rec-prev]');
      const next = host.querySelector('[data-v22-rec-next]');
      syncRecommendationControls = () => {
        const max = Math.max(0, grid.scrollWidth - grid.clientWidth);
        host.querySelector('.v22-cart-carousel-controls').hidden = max <= 2;
        previous.disabled = grid.scrollLeft <= 2;
        next.disabled = grid.scrollLeft >= max - 2;
      };
      const move = direction => {
        const step = grid.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(grid).columnGap) || 0);
        grid.scrollBy({left: direction * step, behavior: reduceMotion ? 'auto' : 'smooth'});
      };
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      grid.addEventListener('scroll', syncRecommendationControls, {passive:true});
      grid.addEventListener('keydown', event => {
        if (event.target !== grid || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') grid.scrollTo({left:event.key === 'Home' ? 0 : grid.scrollWidth,behavior:reduceMotion ? 'auto' : 'smooth'});
        else move(event.key === 'ArrowRight' ? 1 : -1);
      });
      requestAnimationFrame(syncRecommendationControls);
    } catch (_) { /* No real recommendation response means no recommendation block. */ }
  };
  const renderV22Cart = (cart) => {
    v22Summary.hidden = !cart.items.length;
    if (!cart.items.length) { v22Summary.replaceChildren(); recommendationRevision++; return false; }
    cartContent.innerHTML = `<div class="po-cart-lines">${cart.items.map(line => `<article class="po-cart-line" data-cart-key="${escapeHtml(line.key)}">${line.image ? `<img src="${escapeHtml(imageUrl(line.image,240))}" alt="${escapeHtml(line.product_title)}" width="100" height="100">` : '<span></span>'}<div class="po-cart-line__info"><a href="${escapeHtml(previewProductUrl(line.url))}">${escapeHtml(line.product_title)}</a>${line.variant_title && line.variant_title !== 'Default Title' ? `<small>${escapeHtml(line.variant_title)}</small>` : ''}<strong>${money(line.final_line_price)}</strong>${line.original_line_price > line.final_line_price ? `<s>${money(line.original_line_price)}</s>` : ''}<div class="po-cart-line__controls"><div class="po-cart-quantity"><button type="button" data-po-cart-quantity="-1" aria-label="${escapeHtml(cartDrawer.dataset.decrease)}">−</button><input type="number" min="0" step="1" value="${line.quantity}" aria-label="${escapeHtml(cartDrawer.dataset.quantity)}"><button type="button" data-po-cart-quantity="1" aria-label="${escapeHtml(cartDrawer.dataset.increase)}">+</button></div><button type="button" class="po-cart-remove" data-po-cart-remove>${escapeHtml(cartDrawer.dataset.remove)}</button></div></div></article>`).join('')}</div><aside class="v22-cart-recommendations" data-v22-recommendations hidden></aside>`;
    const discounts = (cart.cart_level_discount_applications || []).map(discount => `<div class="po-cart-summary__subtotal"><span>${escapeHtml(discount.title)}</span><span>−${money(discount.total_allocated_amount)}</span></div>`).join('');
    v22Summary.innerHTML = `<div class="po-cart-summary">${discounts}<div class="po-cart-summary__subtotal"><span>${escapeHtml(cartDrawer.dataset.subtotal)}</span><strong>${money(cart.total_price)} ${escapeHtml(cart.currency)}</strong></div><details class="v22-cart-note"><summary>${escapeHtml(cartDrawer.dataset.noteLabel)}</summary><label class="sr-only" for="V22CartNote">${escapeHtml(cartDrawer.dataset.noteLabel)}</label><textarea id="V22CartNote" data-v22-cart-note rows="2">${escapeHtml(cart.note || '')}</textarea><button type="button" data-v22-save-note>${escapeHtml(cartDrawer.dataset.saveLabel)}</button></details><p class="po-cart-summary__shipping">${escapeHtml(cartDrawer.dataset.shipping)}</p><div class="po-cart-summary__actions"><a href="${escapeHtml(cartDrawer.dataset.cartPageUrl)}">${escapeHtml(cartDrawer.dataset.continueLabel)}</a><a href="${escapeHtml(cartDrawer.dataset.checkoutUrl)}">${escapeHtml(cartDrawer.dataset.checkoutLabel)}</a></div></div>`;
    cartRecommendations(cart);
    return true;
  };
  const renderCart = (cart) => {
    doc.querySelectorAll('[data-po-cart-count]').forEach((counter) => { counter.textContent = cart.item_count; });
    if (!cartContent || !cartDrawer) return;
    if (v22Summary && renderV22Cart(cart)) return;
    if (!cart.items.length) {
      cartContent.innerHTML = `<div class="po-cart-empty"><div><h3>${escapeHtml(cartDrawer.dataset.emptyTitle)}</h3><p>${escapeHtml(cartDrawer.dataset.emptyText)}</p><a href="${escapeHtml(cartDrawer.dataset.shopUrl)}">${escapeHtml(cartDrawer.dataset.shopLabel)}</a></div></div>`;
      return;
    }
    const lines = cart.items.map((line) => `<article class="po-cart-line" data-cart-key="${escapeHtml(line.key)}">${line.image ? `<img src="${escapeHtml(imageUrl(line.image, 200))}" alt="${escapeHtml(line.product_title)}" width="92" height="92" loading="lazy">` : ''}<div class="po-cart-line__info"><a href="${escapeHtml(previewProductUrl(line.url))}">${escapeHtml(line.product_title)}</a><strong>${money(line.final_line_price)}</strong>${line.variant_title && line.variant_title !== 'Default Title' ? `<small>${escapeHtml(line.variant_title)}</small>` : ''}<div class="po-cart-line__controls"><div class="po-cart-quantity"><button type="button" data-po-cart-quantity="-1" aria-label="${escapeHtml(cartDrawer.dataset.decrease)}">−</button><input type="number" min="0" value="${line.quantity}" aria-label="${escapeHtml(cartDrawer.dataset.quantity)}"><button type="button" data-po-cart-quantity="1" aria-label="${escapeHtml(cartDrawer.dataset.increase)}">+</button></div><button type="button" class="po-cart-remove" data-po-cart-remove>${escapeHtml(cartDrawer.dataset.remove)}</button></div></div></article>`).join('');
    cartContent.innerHTML = `<div class="po-cart-lines">${lines}</div><div class="po-cart-summary"><div class="po-cart-summary__subtotal"><span>${escapeHtml(cartDrawer.dataset.subtotal)}</span><strong>${money(cart.total_price)}</strong></div><p class="po-cart-summary__shipping">${escapeHtml(cartDrawer.dataset.shipping)}</p><div class="po-cart-summary__actions"><a href="${escapeHtml(cartDrawer.dataset.cartPageUrl)}">${escapeHtml(cartDrawer.dataset.continueLabel)}</a><a href="${escapeHtml(cartDrawer.dataset.checkoutUrl)}">${escapeHtml(cartDrawer.dataset.checkoutLabel)}</a></div></div>`;
    if (cartRecommendation) cartContent.append(cartRecommendation.content.cloneNode(true));
  };
  const refreshCart = () => fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json()).then((cart) => { renderCart(cart); return cart; });
  const openCart = (trigger) => refreshCart().then(() => openDrawer(cartDrawer, trigger));

  const updateDrawerCart = async (endpoint, payload, focusKey = null, focusAction = null) => {
    if (cartBusy) return;
    cartBusy = true;
    cartDrawer.setAttribute('aria-busy', 'true');
    if (v22CartError) v22CartError.textContent = '';
    cartDrawer.querySelectorAll('[data-po-cart-quantity], [data-po-cart-remove], [data-v22-save-note], .po-cart-quantity input').forEach(control => { control.disabled = true; });
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/${endpoint}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : cartDrawer.dataset.errorLabel || 'Cart update failed');
      renderCart(data);
      const line = [...cartDrawer.querySelectorAll('[data-cart-key]')].find(item => item.dataset.cartKey === focusKey);
      const target = line?.querySelector(focusAction || 'input') || cartDrawer.querySelector('.po-drawer__close');
      if (target && 'disabled' in target) target.disabled = false;
      if (cartDrawer.dataset.open === 'true') target?.focus({preventScroll:true});
    } catch (error) {
      if (v22CartError) v22CartError.textContent = error.message || cartDrawer.dataset.errorLabel;
    } finally {
      cartBusy = false;
      cartDrawer.removeAttribute('aria-busy');
      cartDrawer.querySelectorAll('[data-po-cart-quantity], [data-po-cart-remove], [data-v22-save-note], .po-cart-quantity input').forEach(control => { control.disabled = false; });
    }
  };
  cartDrawer?.addEventListener('click', (event) => {
    if (cartBusy && event.target.closest('a, [data-po-cart-quantity], [data-po-cart-remove], [data-v22-save-note]')) { event.preventDefault(); return; }
    if (event.target.closest('[data-v22-save-note]')) {
      updateDrawerCart('update', { note: cartDrawer.querySelector('[data-v22-cart-note]').value }); return;
    }
    const line = event.target.closest('[data-cart-key]');
    if (!line) return;
    const quantityButton = event.target.closest('[data-po-cart-quantity]');
    const remove = event.target.closest('[data-po-cart-remove]');
    if (!quantityButton && !remove) return;
    const input = line.querySelector('input[type="number"]');
    const quantity = remove ? 0 : Math.max(0, Number(input.value || 0) + Number(quantityButton.dataset.poCartQuantity));
    updateDrawerCart('change', {id:line.dataset.cartKey,quantity},line.dataset.cartKey, quantityButton ? `[data-po-cart-quantity="${quantityButton.dataset.poCartQuantity}"]` : null);
  });
  cartDrawer?.addEventListener('change', (event) => {
    const input = event.target.closest('.po-cart-quantity input');
    if (!input || cartBusy || !input.reportValidity()) return;
    const quantity = Number(input.value);
    if (!Number.isInteger(quantity) || quantity < 0) return;
    const line = input.closest('[data-cart-key]');
    updateDrawerCart('change', {id:line.dataset.cartKey,quantity},line.dataset.cartKey);
  });

  const addForm = (form, trigger) => {
    if (form.dataset.poAdding === 'true' || cartBusy) return;
    form.dataset.poAdding = 'true';
    const button = trigger || form.querySelector('button[type="submit"]');
    const iconOnly = button?.hasAttribute('data-po-icon-button');
    const original = button?.innerHTML;
    if (button) {
      button.disabled = true;
      if (iconOnly) button.setAttribute('aria-busy', 'true');
      else button.textContent = form.dataset.loading || 'Adding…';
    }
    return fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
      .then((response) => response.ok ? response.json() : response.json().then((data) => Promise.reject(new Error(data.description || 'Add failed'))))
      .then(() => openCart(trigger || button))
      .then(() => {
        if (button) {
          button.disabled = false;
          if (iconOnly) button.removeAttribute('aria-busy');
          else if (form.dataset.added) button.textContent = form.dataset.added;
          else button.innerHTML = original;
        }
      })
      .catch((error) => {
        const status = form.querySelector('[role="status"]');
        if (status) status.textContent = error.message;
        if (button) {
          button.disabled = false;
          if (iconOnly) button.removeAttribute('aria-busy');
          else button.innerHTML = original;
        }
      }).finally(() => { delete form.dataset.poAdding; });
  };

  doc.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-po-ajax-add]');
    if (!form) return;
    event.preventDefault();
    addForm(form, event.submitter || form.querySelector('button[type="submit"]'));
  });

  doc.querySelectorAll('[data-po-product-card]').forEach((card) => {
    const primary = card.querySelector('.po-v2-card__image--primary, .po-card__image--primary');
    const formInputs = card.querySelectorAll('input[name="id"]');
    const cardPrice = card.querySelector('[data-po-card-price]');
    const cardSelected = card.querySelector('[data-po-card-selected]');
    const cardBand = card.querySelector('[data-po-card-band]');
    const cardLinks = card.querySelectorAll('[data-po-card-link], .po-v2-card__media');
    card.querySelectorAll('[data-po-card-variant]').forEach((button) => button.addEventListener('click', () => {
      card.querySelectorAll('[data-po-card-variant]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      formInputs.forEach((input) => { input.value = button.dataset.variantId; });
      if (button.dataset.image && primary) {
        primary.src = button.dataset.image;
        primary.removeAttribute('srcset');
        if (button.dataset.imageAlt) primary.alt = button.dataset.imageAlt;
      }
      if (button.dataset.price && cardPrice) cardPrice.textContent = button.dataset.price;
      if (button.dataset.priceCents) card.dataset.priceCents = button.dataset.priceCents;
      if (cardSelected) cardSelected.textContent = button.textContent.trim();
      if (cardBand) cardBand.textContent = button.textContent.trim();
      if (button.dataset.url) cardLinks.forEach((link) => { link.href = button.dataset.url; });
    }));
  });

  doc.querySelectorAll('[data-po-catalog]').forEach((catalog) => {
    const grid = catalog.querySelector('#ProductGridContainer');
    const cards = grid ? [...grid.querySelectorAll('[data-po-product-card]')] : [];
    const filterRoots = [...doc.querySelectorAll('[data-po-catalog-filters]')];
    const sort = catalog.querySelector('[data-po-catalog-sort]');
    const counter = catalog.querySelector('[data-po-visible-count]');
    if (!grid || !cards.length) return;

    const state = { experience: 'all', locks: 'all', tools: 'all' };
    const filterName = (input) => input.dataset.filterName || input.name;
    const lockMatches = (actual, selected) => {
      const count = Number(actual);
      if (selected === 'all') return true;
      if (selected === '1-2') return count >= 1 && count <= 2;
      if (selected === '3-4') return count >= 3 && count <= 4;
      return String(count) === selected;
    };
    const sortCards = () => {
      const mode = sort?.value || 'featured';
      const sorted = [...cards].sort((first, second) => {
        if (mode === 'price-ascending') return Number(first.dataset.priceCents) - Number(second.dataset.priceCents);
        if (mode === 'price-descending') return Number(second.dataset.priceCents) - Number(first.dataset.priceCents);
        if (mode === 'title-ascending') return first.dataset.title.localeCompare(second.dataset.title);
        return Number(first.dataset.featuredOrder) - Number(second.dataset.featuredOrder);
      });
      sorted.forEach((card) => grid.append(card));
    };
    const applyFilters = () => {
      let visible = 0;
      cards.forEach((card) => {
        const matches = (state.experience === 'all' || card.dataset.level === state.experience)
          && lockMatches(card.dataset.locks, state.locks)
          && (state.tools === 'all' || card.dataset.tools === state.tools);
        card.hidden = !matches;
        if (matches) visible += 1;
      });
      const unfiltered = Object.values(state).every((value) => value === 'all');
      if (counter) counter.textContent = unfiltered ? counter.dataset.total : String(visible);
      sortCards();
    };
    const syncInputs = (changed) => {
      const name = filterName(changed);
      filterRoots.forEach((rootElement) => rootElement.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (filterName(input) === name) input.checked = input.value === changed.value;
      }));
    };

    filterRoots.forEach((rootElement) => {
      rootElement.addEventListener('change', (event) => {
        const input = event.target.closest('input[type="radio"]');
        if (!input) return;
        const name = filterName(input);
        if (!(name in state)) return;
        state[name] = input.value;
        syncInputs(input);
        applyFilters();
      });
      rootElement.querySelector('[data-po-filter-reset]')?.addEventListener('click', () => {
        Object.keys(state).forEach((key) => { state[key] = 'all'; });
        rootElement.querySelectorAll('input[type="radio"][value="all"]').forEach((input) => { input.checked = true; syncInputs(input); });
        applyFilters();
      });
    });
    sort?.addEventListener('change', sortCards);
    doc.querySelector('[data-po-filter-drawer-apply]')?.addEventListener('click', (event) => closeDrawer(event.currentTarget.closest('[data-po-drawer]')));
    applyFilters();
  });

  const quickDrawer = doc.querySelector('[data-po-quick-view-drawer]');
  const quickContent = quickDrawer?.querySelector('[data-po-quick-view-content]');
  const renderQuickView = (product) => {
    if (!quickContent || !quickDrawer) return;
    const variants = product.variants || [];
    const selected = variants.find((variant) => variant.available) || variants[0];
    const image = selected?.featured_image?.src || product.featured_image;
    quickContent.innerHTML = `<div class="po-quick-view"><div class="po-quick-view__media">${image ? `<img src="${escapeHtml(imageUrl(image, 1200))}" alt="${escapeHtml(product.title)}" width="800" height="800">` : ''}</div><div class="po-quick-view__info"><p class="po-eyebrow">${escapeHtml(quickDrawer.dataset.eyebrow)}</p><h3>${escapeHtml(product.title)}</h3><div class="po-quick-view__price" data-po-quick-price>${money(selected?.price)}</div><div class="po-quick-view__variants">${variants.map((variant) => `<button type="button" data-po-quick-variant data-id="${variant.id}" data-price="${variant.price}" data-image="${escapeHtml(variant.featured_image?.src || '')}" aria-pressed="${variant.id === selected?.id}" ${variant.available ? '' : 'disabled'}>${escapeHtml(variant.title)}</button>`).join('')}</div><form data-po-ajax-add data-loading="${escapeHtml(quickDrawer.dataset.loading)}" data-added="${escapeHtml(quickDrawer.dataset.added)}"><input type="hidden" name="id" value="${selected?.id || ''}"><div class="po-quick-view__buy"><div class="po-product__quantity"><button type="button" data-po-quantity="-1" aria-label="${escapeHtml(quickDrawer.dataset.decrease)}">−</button><input type="number" name="quantity" value="1" min="1" aria-label="${escapeHtml(quickDrawer.dataset.quantity)}"><button type="button" data-po-quantity="1" aria-label="${escapeHtml(quickDrawer.dataset.increase)}">+</button></div><button class="po-product__add" type="submit">${escapeHtml(quickDrawer.dataset.add)}</button></div><p role="status" class="po-product__status"></p></form></div></div>`;
    const mainImage = quickContent.querySelector('img');
    const price = quickContent.querySelector('[data-po-quick-price]');
    const input = quickContent.querySelector('input[name="id"]');
    quickContent.querySelectorAll('[data-po-quick-variant]').forEach((button) => button.addEventListener('click', () => {
      quickContent.querySelectorAll('[data-po-quick-variant]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      input.value = button.dataset.id;
      price.textContent = money(button.dataset.price);
      if (button.dataset.image && mainImage) mainImage.src = imageUrl(button.dataset.image, 1200);
    }));
  };

  doc.addEventListener('click', (event) => {
    const quick = event.target.closest('[data-po-quick-view]');
    if (!quick || !quickDrawer) return;
    event.preventDefault();
    quick.setAttribute('aria-busy', 'true');
    fetch(`/products/${encodeURIComponent(quick.dataset.handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Quick View failed')))
      .then((product) => { renderQuickView(product); openDrawer(quickDrawer, quick); })
      .finally(() => quick.removeAttribute('aria-busy'));
  });

  doc.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-po-quantity]');
    if (!quantityButton) return;
    const wrapper = quantityButton.closest('.po-product__quantity');
    const input = wrapper?.querySelector('input[type="number"]');
    if (!input) return;
    input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(quantityButton.dataset.poQuantity));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  doc.querySelectorAll('[data-po-sort-menu]').forEach((sort) => {
    const button = sort.querySelector('[data-po-sort-trigger]');
    const select = sort.querySelector('select[name="sort_by"]');
    button?.addEventListener('click', () => { sort.dataset.open = String(sort.dataset.open !== 'true'); });
    sort.querySelectorAll('input[type="radio"]').forEach((radio) => radio.addEventListener('change', () => {
      select.value = radio.value;
      select.form?.submit();
    }));
    doc.addEventListener('pointerdown', (event) => { if (!sort.contains(event.target)) sort.dataset.open = 'false'; });
  });

  doc.querySelectorAll('[data-po-tabs]').forEach((tabsRoot) => {
    tabsRoot.classList.add('po-tabs');
    const tabs = [...tabsRoot.querySelectorAll('[role="tab"]')];
    const panels = tabs.map((tab) => doc.getElementById(tab.getAttribute('aria-controls')));
    const activate = (index, moveFocus = false) => {
      tabs.forEach((tab, tabIndex) => { tab.setAttribute('aria-selected', String(index === tabIndex)); tab.tabIndex = index === tabIndex ? 0 : -1; });
      panels.forEach((panel, panelIndex) => {
        if (!panel) return;
        panel.hidden = panelIndex !== index;
        if (panelIndex === index) {
          panel.classList.add('is-entering');
          requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.remove('is-entering')));
        }
      });
      if (moveFocus) tabs[index]?.focus();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowRight', 'ArrowLeft', 'Home', 'End', 'Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Enter' || event.key === ' ') { activate(index); return; }
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        activate(next, true);
      });
    });
    activate(Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));
  });

  doc.querySelectorAll('[data-po-product]').forEach((productRoot) => {
    const lightbox = productRoot.querySelector('[data-po-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-po-lightbox-image]');
    const mediaButtons = [...productRoot.querySelectorAll('[data-po-media-open]')];
    let mediaIndex = 0;
    const showMedia = (index) => {
      if (!mediaButtons.length || !lightboxImage) return;
      mediaIndex = (index + mediaButtons.length) % mediaButtons.length;
      lightboxImage.src = mediaButtons[mediaIndex].dataset.src;
      lightboxImage.alt = mediaButtons[mediaIndex].dataset.alt || '';
    };
    mediaButtons.forEach((button, index) => button.addEventListener('click', () => { showMedia(index); lightbox?.showModal(); }));
    lightbox?.querySelector('[data-po-lightbox-close]')?.addEventListener('click', () => lightbox.close());
    lightbox?.querySelector('[data-po-lightbox-prev]')?.addEventListener('click', () => showMedia(mediaIndex - 1));
    lightbox?.querySelector('[data-po-lightbox-next]')?.addEventListener('click', () => showMedia(mediaIndex + 1));
    lightbox?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showMedia(mediaIndex - 1);
      if (event.key === 'ArrowRight') showMedia(mediaIndex + 1);
    });
    lightbox?.addEventListener('click', (event) => { if (event.target === lightbox) lightbox.close(); });

    const gallery = productRoot.querySelector('[data-po-gallery]');
    const dots = [...productRoot.querySelectorAll('[data-po-gallery-dot]')];
    if (gallery && dots.length) {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = Number(entry.target.dataset.mediaIndex || 0);
        dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(index === dotIndex)));
      }), { root: gallery, threshold: .65 });
      productRoot.querySelectorAll('[data-po-product-media]').forEach((media) => observer.observe(media));
      dots.forEach((dot, index) => dot.addEventListener('click', () => productRoot.querySelector(`[data-media-index="${index}"]`)?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', inline: 'center', block: 'nearest' })));
    }

    const price = productRoot.querySelector('[data-po-price]');
    const selectionPrice = productRoot.querySelector('[data-po-selection-price]');
    const stockState = productRoot.querySelector('.stock-state');
    const variantId = productRoot.querySelector('[data-po-product-form] input[name="id"]');
    const primaryButton = productRoot.querySelector('[data-po-product-form] button[type="submit"]');
    productRoot.querySelectorAll('[data-po-variant-option]').forEach((option) => option.addEventListener('change', () => {
      if (!option.checked) return;
      if (variantId) variantId.value = option.value;
      if (price) {
        const comparePrice = option.dataset.comparePrice;
        price.innerHTML = comparePrice ? `${escapeHtml(option.dataset.price)} <s>${escapeHtml(comparePrice)}</s>` : escapeHtml(option.dataset.price);
      }
      if (selectionPrice) selectionPrice.textContent = option.dataset.price;
      const available = option.dataset.available === 'true';
      if (primaryButton) {
        primaryButton.disabled = !available;
        primaryButton.textContent = available ? productRoot.dataset.addLabel : productRoot.dataset.soldOutLabel;
      }
      if (stockState) {
        stockState.classList.toggle('is-sold-out', !available);
        stockState.textContent = available ? productRoot.dataset.inStockLabel : productRoot.dataset.soldOutLabel;
      }
      productRoot.querySelectorAll('[data-po-current-configuration]').forEach((label) => { label.textContent = option.dataset.title; });
      const url = new URL(location.href);
      url.searchParams.set('variant', option.value);
      history.replaceState({}, '', url);
      if (option.dataset.image) {
        const target = productRoot.querySelector(`[data-media-id="${option.dataset.mediaId}"]`) || productRoot.querySelector('[data-po-product-media]');
        target?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
      }
      productRoot.dispatchEvent(new CustomEvent('product:variant-change', {
        bubbles: true,
        detail: {
          id: option.value,
          title: option.dataset.title,
          price: option.dataset.price,
          image: option.dataset.image || '',
          imageAlt: option.dataset.imageAlt || '',
          available
        }
      }));
    }));
  });

  doc.querySelectorAll('[data-po-sticky]').forEach((sticky) => {
    const source = doc.querySelector('[data-po-primary-buy]');
    if (!source) return;
    const observer = new IntersectionObserver(([entry]) => { sticky.dataset.visible = String(!entry.isIntersecting); }, { threshold: .15 });
    observer.observe(source);
    doc.addEventListener('product:variant-change', (event) => {
      const form = sticky.querySelector('[data-po-sticky-form]');
      const variantId = form?.querySelector('input[name="id"]');
      const price = sticky.querySelector('[data-po-sticky-price]');
      const image = sticky.querySelector('img');
      const button = sticky.querySelector('[data-po-sticky-button]');
      if (variantId) variantId.value = event.detail.id;
      if (price) price.textContent = event.detail.price;
      if (image && event.detail.image) {
        image.src = event.detail.image;
        image.alt = event.detail.imageAlt;
        image.removeAttribute('srcset');
      }
      if (button) {
        button.disabled = !event.detail.available;
        button.textContent = event.detail.available ? event.target.dataset.addLabel : event.target.dataset.soldOutLabel;
      }
    });
    sticky.querySelector('form')?.addEventListener('submit', (event) => { event.preventDefault(); addForm(event.currentTarget, event.currentTarget.querySelector('button')); });
  });

  const backTop = doc.querySelector('[data-po-back-top]');
  if (backTop) {
    const update = () => { backTop.dataset.visible = String(window.scrollY > 900); };
    addEventListener('scroll', update, { passive: true });
    backTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));
    update();
  }
})();
